import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Package, Undo2, UserRound } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, personName } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { Page, Part, PartAssignment, PersonSummary, Room } from '../lib/types';
import { PersonPicker, RoomPicker } from './pickers';
import { useToast } from './toast';
import { Alert, Button, Card, CardHeader, Field, Input, Modal, Segmented, Select, Spinner, Switch, Textarea } from './ui';

type StockPart = Pick<Part, 'id' | 'name' | 'quantityOnHand'>;

type AssigneeChoice = { type: 'PERSON'; person: PersonSummary | null } | { type: 'ROOM'; room: Room | null };

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

export function StockDialog({ open, onClose, part }: { open: boolean; onClose(): void; part: StockPart }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'receive' | 'count'>('receive');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setMode('receive');
      setQuantity('');
      setNote('');
      setError('');
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api.post(
        `/parts/${part.id}/stock`,
        mode === 'receive' ? { mode, quantity: Number(quantity), note } : { mode, quantityOnHand: Number(quantity), note },
      ),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(mode === 'receive' ? `Added ${quantity} to stock` : 'Stock count saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Update stock"
      description={part.name}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="stock-form" loading={save.isPending} disabled={quantity === ''}>
            Save
          </Button>
        </>
      }
    >
      <form id="stock-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Segmented
          value={mode}
          onChange={(value) => setMode(value)}
          options={[
            { value: 'receive', label: 'Receive stock' },
            { value: 'count', label: 'Count the shelf' },
          ]}
        />
        <Field label={mode === 'receive' ? 'Units received' : 'Units on the shelf'} hint={`${part.quantityOnHand} on hand now`} required>
          <Input
            type="number"
            inputMode="numeric"
            min={mode === 'receive' ? 1 : 0}
            max={1_000_000}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-32"
            autoFocus
            required
          />
        </Field>
        <Field label="Note">
          <Input
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === 'receive' ? 'e.g. PO-2026-1142' : 'e.g. Summer inventory'}
          />
        </Field>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Assign & return
// ---------------------------------------------------------------------------

/** Assigns units of a part to a person or room. Either the part or the assignee can be fixed. */
export function AssignPartDialog({
  open,
  onClose,
  part,
  assignee: fixedAssignee,
}: {
  open: boolean;
  onClose(): void;
  part?: StockPart | null;
  assignee?: AssigneeChoice;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [partId, setPartId] = useState('');
  const [assignee, setAssignee] = useState<AssigneeChoice>(fixedAssignee ?? { type: 'PERSON', person: null });
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPartId('');
      setAssignee(fixedAssignee ?? { type: 'PERSON', person: null });
      setQuantity('1');
      setNotes('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const inStock = useQuery({
    queryKey: ['parts', 'assignable'],
    queryFn: () => api.get<Page<Part>>('/parts', { stock: 'in', kind: 'ACCESSORY', pageSize: 200 }),
    enabled: open && !part,
  });

  const chosen = part ?? inStock.data?.items.find((p) => p.id === partId) ?? null;
  const assigneeInput =
    assignee.type === 'PERSON'
      ? assignee.person && { type: 'PERSON', personId: assignee.person.id }
      : assignee.room && { type: 'ROOM', roomId: assignee.room.id };
  const target = fixedAssignee
    ? fixedAssignee.type === 'PERSON'
      ? fixedAssignee.person && personName(fixedAssignee.person)
      : fixedAssignee.room?.name
    : null;

  const save = useMutation({
    mutationFn: () => api.post(`/parts/${chosen!.id}/assign`, { quantity: Number(quantity), assignee: assigneeInput, notes }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Assigned ${quantity} × ${chosen!.name}`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (chosen && assigneeInput) save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={part ? `Assign ${part.name}` : target ? `Assign a part to ${target}` : 'Assign a part'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="assign-part-form"
            loading={save.isPending}
            disabled={!chosen || !assigneeInput || !Number(quantity)}
          >
            Assign
          </Button>
        </>
      }
    >
      <form id="assign-part-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        {!part && (
          <Field label="Part" required hint={inStock.data?.items.length === 0 ? 'No accessories are in stock right now' : undefined}>
            <Select value={partId} onChange={(e) => setPartId(e.target.value)} required>
              <option value="">{inStock.isLoading ? 'Loading…' : 'Choose an accessory'}</option>
              {inStock.data?.items.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                  {option.partNumber ? ` (${option.partNumber})` : ''} · {option.quantityOnHand} in stock
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!fixedAssignee && (
          <div className="space-y-3">
            <span className="block text-sm font-medium text-slate-700">Assign to</span>
            <Segmented
              value={assignee.type}
              onChange={(type) => setAssignee(type === 'PERSON' ? { type, person: null } : { type, room: null })}
              options={[
                { value: 'PERSON', label: 'Person', icon: <UserRound /> },
                { value: 'ROOM', label: 'Room', icon: <DoorOpen /> },
              ]}
            />
            {assignee.type === 'PERSON' ? (
              <PersonPicker inline autoFocus value={assignee.person} onChange={(person) => setAssignee({ type: 'PERSON', person })} />
            ) : (
              <RoomPicker inline value={assignee.room} onChange={(room) => setAssignee({ type: 'ROOM', room })} />
            )}
          </div>
        )}
        <Field label="Quantity" hint={chosen ? `${chosen.quantityOnHand} in stock` : undefined} required>
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={chosen?.quantityOnHand || undefined}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-28"
            required
          />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Includes power cable" />
        </Field>
      </form>
    </Modal>
  );
}

export function ReturnPartDialog({ assignment, onClose }: { assignment: PartAssignment | null; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [restock, setRestock] = useState(true);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (assignment) {
      setRestock(true);
      setNote('');
    }
  }, [assignment]);

  const save = useMutation({
    mutationFn: () => api.post(`/part-assignments/${assignment!.id}/return`, { returnToStock: restock, note }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(restock ? 'Returned to stock' : 'Marked as returned');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      open={!!assignment}
      onClose={onClose}
      size="sm"
      title={`Return ${assignment?.part.name ?? 'part'}`}
      description={assignment && `${assignment.quantity} × from ${assignment.assigneeLabel}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Return
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Switch checked={restock} onChange={setRestock} label="Put back in stock" description="Turn this off if it came back broken or never came back" />
        <Field label="Note">
          <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Person & room pages
// ---------------------------------------------------------------------------

export function AssignedPartsCard({ assignee }: { assignee: { type: 'PERSON'; person: PersonSummary } | { type: 'ROOM'; room: Room } }) {
  const { can } = useAuth();
  const canWrite = can('TECHNICIAN');
  const [assigning, setAssigning] = useState(false);
  const [returning, setReturning] = useState<PartAssignment | null>(null);
  const filter = assignee.type === 'PERSON' ? { personId: assignee.person.id } : { roomId: assignee.room.id };
  const assignments = useQuery({
    queryKey: ['part-assignments', filter],
    queryFn: () => api.get<PartAssignment[]>('/part-assignments', filter),
  });

  return (
    <Card>
      <CardHeader
        title="Accessories"
        description={`Keyboards, mice, docks, and other parts assigned to this ${assignee.type === 'PERSON' ? 'person' : 'room'}`}
        actions={
          canWrite && (
            <Button size="sm" icon={<Package />} onClick={() => setAssigning(true)}>
              Assign a part
            </Button>
          )
        }
      />
      {assignments.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : !assignments.data?.length ? (
        <p className="px-5 py-6 text-sm text-slate-500">No parts assigned.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {assignments.data.map((assignment) => (
            <li key={assignment.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <Link to={`/parts/${assignment.part.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                  {assignment.quantity > 1 && `${assignment.quantity} × `}
                  {assignment.part.name}
                </Link>
                <p className="text-xs text-slate-500">
                  Since {formatDate(assignment.assignedAt)}
                  {assignment.part.partNumber && ` · ${assignment.part.partNumber}`}
                  {assignment.notes && ` · ${assignment.notes}`}
                </p>
              </div>
              {canWrite && (
                <Button size="sm" icon={<Undo2 />} onClick={() => setReturning(assignment)}>
                  Return
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <AssignPartDialog open={assigning} onClose={() => setAssigning(false)} assignee={assignee} />
      <ReturnPartDialog assignment={returning} onClose={() => setReturning(null)} />
    </Card>
  );
}
